/**
 * Championship prizes — one record per finishing position, made of one or
 * more parts (owner, 31 Oct):
 *
 *   items: [ { type: 'cash',    amount: 500 },          // $500 cash
 *            { type: 'credits', amount: 2000 },         // 2,000 Whapaxx credits
 *            { type: 'item',    name: 'Racing wheel' } ] // a physical prize
 *   image:   optional picture of the prize (S3 URL)
 *   details: optional extra line ("Presented at the finale")
 *
 * `description` is still written — a one-line summary of the parts — so
 * anything that only knows the old shape (older app builds) keeps showing
 * the prize. The customer app has a copy of these helpers
 * (wpxg-react/src/utils/prizes.js); keep them the same.
 */

export const PRIZE_TYPES = [
  { key: 'cash', label: 'Cash', icon: '💵' },
  { key: 'credits', label: 'Whapaxx credits', icon: '🎮' },
  { key: 'item', label: 'Physical prize', icon: '🎁' },
];

const num = v => {
  const n = Number(String(v ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** One part as a short phrase: "$500 cash", "2,000 Whapaxx credits", "Racing wheel". */
export function prizePartText(part) {
  const p = part || {};
  if (p.type === 'cash') return `$${num(p.amount).toLocaleString('en-US', { maximumFractionDigits: 2 })} cash`;
  if (p.type === 'credits') return `${num(p.amount).toLocaleString('en-US', { maximumFractionDigits: 0 })} Whapaxx credits`;
  return String(p.name || '').trim();
}

/** A prize's parts, from the new shape or (older records) the description. */
export function prizeParts(prize) {
  const items = Array.isArray(prize?.items) ? prize.items : [];
  return items.filter(p => prizePartText(p));
}

/** The one-line summary: "$500 cash + 2,000 Whapaxx credits + Racing wheel". */
export function prizeSummary(prize) {
  const parts = prizeParts(prize).map(prizePartText);
  return parts.length ? parts.join(' + ') : String(prize?.description || '').trim();
}

/** Tidy the editor's rows into what is saved; drops empty rows. */
export function cleanPrizeItems(rows) {
  return (rows || []).map((r) => {
    if (r.type === 'cash' || r.type === 'credits') {
      const amount = num(r.amount);
      return amount > 0 ? { type: r.type, amount } : null;
    }
    const name = String(r.name || '').trim();
    return name ? { type: 'item', name } : null;
  }).filter(Boolean);
}
